// File: src/components/modal/TicketCreateModal.tsx

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { TicketImageInterface, TicketInterface } from "../../common/interfaces";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  deleteTicketImage,
  listTicketImages,
  uploadTicketImages
} from "../../services/ticketImage.service";
import { PriorityLevel, PriorityLevelLabel } from "../enum/priorityEnum";
import PageToaster from "../toaster/PageToaster";

interface TicketCreateModalProps {
  open: boolean;
  onClose: () => void;
  onTicketSaved: () => void;
  ticket?: TicketInterface | null;
}

const MAX_IMAGES = 3;
const ACCEPTED_TYPES = ["image/png", "image/jpeg"];

interface PendingImage {
  file: File;
  previewUrl: string;
}

const TicketCreateModal: React.FC<TicketCreateModalProps> = ({
  open,
  onClose,
  onTicketSaved,
  ticket
}) => {
  const [ticketTitle, setTicketTitle] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketPriority, setTicketPriority] = useState<PriorityLevel>(
    PriorityLevel.NONE
  );

  const [updateMode, setUpdateMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prints já salvos (modo edição) e os recém colados/escolhidos, ainda só
  // no navegador — só sobem pro servidor quando o ticket é salvo.
  const [existingImages, setExistingImages] = useState<TicketImageInterface[]>(
    []
  );
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const totalImages = existingImages.length + pendingImages.length;

  const containerId = "toaster-ticket-create-modal";
  const { autoSuccess, autoError, autoWarn } = usePageToast(containerId, true);

  const handleHttpError = makeToasterErrorHandler(autoError);

  const clear = () => {
    setUpdateMode(false);
    setTicketTitle("");
    setTicketMessage("");
    // Sem prioridade default o pedido nasce sinalizado como "sem pressa";
    // média força quem abre a pensar na urgência antes de criar.
    setTicketPriority(PriorityLevel.MEDIUM);
    setExistingImages([]);
    setPendingImages([]);
  };

  useEffect(() => {
    if (ticket) {
      setUpdateMode(true);
      setTicketTitle(ticket.ticket_title ?? "");
      setTicketMessage(ticket.ticket_message ?? "");
      setTicketPriority(
        (ticket.ticket_priority as PriorityLevel) ?? PriorityLevel.NONE
      );

      if (ticket.ticket_id) {
        listTicketImages(ticket.ticket_id)
          .then(setExistingImages)
          .catch((err) =>
            console.error("Erro ao buscar prints do ticket:", err)
          );
      }
      setPendingImages([]);
    } else {
      clear();
    }
  }, [ticket]);

  const addFiles = (files: File[]) => {
    const accepted = files.filter((f) => ACCEPTED_TYPES.includes(f.type));
    if (!accepted.length) return;

    const room = MAX_IMAGES - existingImages.length - pendingImages.length;
    if (room <= 0) {
      autoWarn(`Máximo de ${MAX_IMAGES} imagens por ticket.`);
      return;
    }

    const toAdd = accepted.slice(0, room);
    if (accepted.length > toAdd.length) {
      autoWarn(
        `Só cabia${toAdd.length === 1 ? "" : "m"} mais ${toAdd.length} imagem(ns) neste ticket.`
      );
    }

    setPendingImages((prev) => [
      ...prev,
      ...toAdd.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }))
    ]);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const images: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) images.push(file);
      }
    }

    if (images.length) {
      e.preventDefault();
      addFiles(images);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = "";
  };

  const removePending = (index: number) => {
    setPendingImages((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExisting = async (image: TicketImageInterface) => {
    if (!ticket?.ticket_id) return;
    try {
      await deleteTicketImage(ticket.ticket_id, image.ticket_image_id);
      setExistingImages((prev) =>
        prev.filter((i) => i.ticket_image_id !== image.ticket_image_id)
      );
    } catch (error) {
      console.error("Erro ao remover print do ticket:", error);
      handleHttpError(error);
    }
  };

  const handleClose = () => {
    pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    clear();
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload: any = {
        ticket_title: ticketTitle,
        ticket_message: ticketMessage,
        ticket_priority: ticketPriority
      };

      let ticketId = ticket?.ticket_id ?? undefined;

      if (updateMode && ticketId) {
        await api.put(`/ticket/${ticketId}`, payload);
      } else {
        const { data } = await api.post("/ticket", payload);
        ticketId = data.ticket_id;
      }

      if (pendingImages.length && ticketId) {
        try {
          await uploadTicketImages(
            ticketId,
            pendingImages.map((p) => p.file)
          );
        } catch (imgError) {
          console.error("Erro ao enviar prints do ticket:", imgError);
          autoWarn("Ticket salvo, mas houve um problema ao enviar as imagens.");
        }
      }

      pendingImages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      autoSuccess(
        updateMode
          ? "Ticket atualizado com sucesso!"
          : "Ticket criado com sucesso!"
      );

      onTicketSaved();
      clear();
      onClose();
    } catch (error: any) {
      console.error("Erro ao salvar ticket:", error.response?.data || error);
      handleHttpError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 3,
            borderRadius: 2,
            boxShadow: 3,
            width: 400,
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h2>{updateMode ? "Editar Ticket" : "Criar Ticket"}</h2>

          <TextField
            fullWidth
            label="Título do Ticket"
            value={ticketTitle}
            onChange={(e) => setTicketTitle(e.target.value)}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Mensagem"
            value={ticketMessage}
            onChange={(e) => setTicketMessage(e.target.value)}
            margin="normal"
            multiline
            minRows={3}
            required
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Prioridade</InputLabel>
            <Select
              value={ticketPriority}
              label="Prioridade"
              onChange={(e) =>
                setTicketPriority(e.target.value as PriorityLevel)
              }
            >
              {(Object.values(PriorityLevel) as PriorityLevel[])
                .filter((p) => p !== PriorityLevel.NONE)
                .map((p) => (
                  <MenuItem key={p} value={p}>
                    {PriorityLevelLabel[p]}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--text-muted)",
                mb: 0.5
              }}
            >
              Prints (opcional, até {MAX_IMAGES})
            </Box>

            <Box
              tabIndex={0}
              onClick={() =>
                totalImages < MAX_IMAGES && fileInputRef.current?.click()
              }
              onPaste={handlePaste}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              sx={{
                p: 2,
                border: "1px dashed var(--border-strong)",
                borderRadius: 2,
                textAlign: "center",
                fontSize: 12.5,
                color: "var(--text-muted)",
                cursor: totalImages >= MAX_IMAGES ? "default" : "pointer",
                opacity: totalImages >= MAX_IMAGES ? 0.5 : 1,
                outline: "none",
                transition: "border-color 0.15s ease",
                "&:focus-visible": { borderColor: "var(--accent)" }
              }}
            >
              {totalImages >= MAX_IMAGES
                ? "Limite de imagens atingido"
                : "Cole aqui com (Ctrl+V) um print, e/ou arraste ou clique para escolher um PNG/JPG"}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                multiple
                hidden
                onChange={handleFileInputChange}
              />
            </Box>

            {totalImages > 0 && (
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1.5 }}
              >
                {existingImages.map((img) => (
                  <Box
                    key={img.ticket_image_id}
                    sx={{ position: "relative", width: 64, height: 64 }}
                  >
                    <Box
                      component="img"
                      src={img.url ?? ""}
                      alt={img.original_name ?? "print"}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 1.5,
                        border: "1px solid var(--border)"
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeExisting(img)}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        width: 20,
                        height: 20,
                        bgcolor: "var(--surface)",
                        border: "1px solid var(--border)",
                        "&:hover": { bgcolor: "var(--surface-2)" }
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                ))}

                {pendingImages.map((p, index) => (
                  <Box
                    key={p.previewUrl}
                    sx={{ position: "relative", width: 64, height: 64 }}
                  >
                    <Box
                      component="img"
                      src={p.previewUrl}
                      alt={p.file.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 1.5,
                        border: "1px dashed var(--accent)"
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removePending(index)}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        width: 20,
                        height: 20,
                        bgcolor: "var(--surface)",
                        border: "1px solid var(--border)",
                        "&:hover": { bgcolor: "var(--surface-2)" }
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Button onClick={handleClose} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {updateMode ? "Atualizar" : "Criar"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default TicketCreateModal;
